import { Clerk } from "@clerk/clerk-js";
import { loadClerkUIScript } from "@clerk/shared/loadClerkJsScript";
import type { Resources } from "@clerk/shared/types";
import {
	Code,
	CreditCard,
	FileText,
	Hash,
	type IconNode,
	Link,
	List,
	Settings,
	Sparkles,
	createElement,
} from "lucide";

import { i18next } from "../i18n";
import type { ClerkAdapter, UserMenuOptions } from "./adapter";
import type { User } from "./model";

/** Pure projection from a Clerk listener emission to the Model's user slice. */
export const toSession = (resources: Resources): User | null => {
	const session = resources.session;
	const user = session?.user;
	if (!session || !user) return null;
	const claims = session.lastActiveToken?.jwt?.claims as
		| { rhysomnAdmin?: unknown }
		| undefined;
	return {
		id: user.id,
		...(resources.organization
			? { organizationId: resources.organization.id }
			: {}),
		name: user.fullName ?? "",
		email: user.primaryEmailAddress?.emailAddress ?? "",
		isAdmin: claims?.rhysomnAdmin === true,
		createdAt: user.createdAt?.toISOString() ?? null,
	};
};

const menuItem = (key: string, href: string, icon: IconNode) => ({
	label: i18next.t(key),
	href,
	mountIcon: (el: HTMLDivElement) => {
		const svg = createElement(icon);
		svg.setAttribute("width", "16");
		svg.setAttribute("height", "16");
		el.replaceChildren(svg);
	},
	unmountIcon: (el?: HTMLDivElement) => el?.replaceChildren(),
});

export const userMenuItems = (options: Omit<UserMenuOptions, "showName">) => [
	menuItem("nav.tags", "/tags", Hash),
	menuItem("nav.attributes", "/attribute-schema", List),
	...(options.billingPreviewEnabled
		? [menuItem("nav.creditUsage", "/billing", CreditCard)]
		: []),
	menuItem("nav.userSettings", "/settings", Settings),
	menuItem("nav.auditLog", "/audit", FileText),
	...(options.integrationsEnabled
		? [menuItem("nav.integrations", "/integrations", Link)]
		: []),
	...(options.isAdmin
		? [
				menuItem("nav.developer", "/developer", Code),
				menuItem("nav.ai", "/ai", Sparkles),
			]
		: []),
];

/** R14: sign-in on `/capture` returns to `/capture` (the payload stash survives the round trip); elsewhere Clerk's default applies. */
export const signInProps = (
	pathname: string,
): { fallbackRedirectUrl?: string } =>
	pathname === "/capture" ? { fallbackRedirectUrl: "/capture" } : {};

/** Call from a boot function, never at module scope: the SDK touches `window`. */
export const makeClerkAdapter = (publishableKey: string): ClerkAdapter => {
	const clerk = new Clerk(publishableKey);
	// `load()` is memoized so every mount waits for the same load; clerk-js throws
	// "Clerk was not loaded with Ui components" when a component mounts before it.
	let loading: Promise<void> | undefined;
	// Clerk 6.30 ships its prebuilt UI separately: `@clerk/ui` is React-bound, so v2 loads
	// Clerk's own UI bundle from its CDN (it carries its own React) and hands the constructor
	// to the core. Without it every `mount*` throws "Clerk was not loaded with Ui components".
	const ensureLoaded = () => {
		loading ??= loadClerkUIScript({ publishableKey }).then(() => {
			const ClerkUI = (
				window as unknown as { __internal_ClerkUICtor?: unknown }
			).__internal_ClerkUICtor;
			return clerk.load(
				ClerkUI ? { ui: { ClerkUI: ClerkUI as never } } : undefined,
			);
		});
		return loading;
	};
	const afterLoad =
		(mount: (element: HTMLDivElement) => void) => (element: HTMLDivElement) => {
			void ensureLoaded().then(() => {
				if (element.isConnected) mount(element);
			});
		};
	return {
		load: ensureLoaded,
		mountSignIn: afterLoad((element) =>
			clerk.mountSignIn(element, signInProps(window.location.pathname)),
		),
		unmountSignIn: (element) => clerk.unmountSignIn(element),
		mountOrganizationSwitcher: afterLoad((element) =>
			clerk.mountOrganizationSwitcher(element),
		),
		unmountOrganizationSwitcher: (element) =>
			clerk.unmountOrganizationSwitcher(element),
		// v1's UserButton.Action items navigate with the router; a plain href does the same.
		// Clerk's UI drops any custom item without an icon, so each one mounts a lucide SVG.
		mountUserButton: (element, options) =>
			afterLoad((host) =>
				clerk.mountUserButton(host, {
					customMenuItems: userMenuItems(options),
					showName: options.showName,
					// Avatar first, then the name, like a sidebar footer.
					appearance: {
						elements: {
							userButtonBox: { flexDirection: "row-reverse" },
							// Clerk pads the name's left for a name-first layout; the flip makes that a gap.
							userButtonOuterIdentifier: { paddingLeft: 0 },
						},
					},
				}),
			)(element),
		unmountUserButton: (element) => clerk.unmountUserButton(element),
		addListener: (listener) =>
			clerk.addListener((resources) => listener(toSession(resources))),
		getToken: async () => (await clerk.session?.getToken()) ?? null,
		signOut: (redirectUrl) => clerk.signOut({ redirectUrl }),
	};
};
